import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";
import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MainCategory } from "src/app/pages/users/q-commerce/models/main-category.model";
import { ButtonConfig, Child, GridChildViewConfig, Variant } from "src/app/pages/users/q-commerce/models/category-group.model";
import { QCommerceService } from "src/app/pages/users/q-commerce/services/q-commerce.service";
import { Cart } from "src/app/pages/users/q-commerce/models/cart.model";
import { EDeliveryService } from "src/app/pages/users/e-delivery/services/e-delivery.service";
import { ServicesService } from "src/app/pages/users/service/services/services.service";

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule],
  selector: "app-product-variant-modal",
  templateUrl: "./product-variant-modal.component.html",
  styleUrl: "./product-variant-modal.component.scss",
  animations: [
    trigger("quantityChangeAnimation", [
      transition(":increment", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.3s ease-in", style({ opacity: 1, transform: "scale(1)" }))]),
      transition(":decrement", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.5s ease-out", style({ opacity: 1, transform: "scale(1)" }))])
    ]),
    trigger("buttonTransition", [
      transition(":enter", [style({ opacity: 0 }), animate("200ms ease-out", style({ opacity: 1 }))]),
      transition(":leave", [animate("200ms ease-in", style({ opacity: 0 }))])
    ])
  ]
})
export class ProductVariantModalComponent {
  @Input() product!: Child;
  @Input() isDelivery: boolean = false;
  @Input() merchantId: string = "";
  @Input() serviceMainCategory!: MainCategory;
  @Output() closeModal = new EventEmitter<void>();
  @Input() gridChildViewConfig!: GridChildViewConfig | null;

  constructor(public helper: Helper, private qcommerceService: QCommerceService, private edeliveryService: EDeliveryService, private servicesService: ServicesService) {}

  /**
   * Emit the closeModal event emitter to close the product variant modal.
   */
  closeProductVariantsModal(): void {
    this.closeModal.emit();
  }

  /**
   * Calculates the discount percentage between the discounted price and the original price.
   * Returns 0 if the original price is 0.
   * @param discountedPrice the discounted price of the product
   * @param originalPrice the original price of the product
   * @returns the discount percentage
   */
  calculateDiscountPercentage(discountedPrice: number, originalPrice: number): number {
    if (originalPrice === 0) {
      return 0;
    }
    const discount = originalPrice - discountedPrice;
    return Math.floor((discount / originalPrice) * 100);
  }

  /**
   * Add a product variant to the cart. If the product variant is already in the cart,
   * it will increment the quantity by 1. Otherwise, it will add the product variant to the cart
   * with a quantity of 1.
   * @param variant the product variant to add to the cart
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  addProductToCart(variant: Variant): void {
    const cartItems: Cart[] = this.getCartItems();
    const cartItem: Cart = {
      productId: this.product._id,
      variantId: variant._id,
      qty: 1
    };
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      cartItem.merchantId = this.merchantId;
    }
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce") || this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      cartItem.price = variant.discountedPrice || variant.price;
    }
    if (this.isDelivery) {
      this.edeliveryService.addToCart(cartItem, variant);
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      if (cartItems.length > 0) {
        const cartServiceItemMainCategoryString = localStorage.getItem("cartServiceItemMainCategory");
        const cartServiceItemMainCategory = cartServiceItemMainCategoryString ? JSON.parse(cartServiceItemMainCategoryString) : "";
        if (cartItems.length > 0 && this.serviceMainCategory._id === cartServiceItemMainCategory._id && !cartServiceItemMainCategory.serviceSetting?.isAllowMultipleService) {
          this.servicesService.removeFromCart(this.product._id, cartItems[0].variantId);
          this.servicesService.addToCart(cartItem, variant, this.serviceMainCategory);
        }
      } else {
        this.servicesService.addToCart(cartItem, variant, this.serviceMainCategory);
      }
    } else {
      this.qcommerceService.addToCart(cartItem, variant);
    }
  }

  /**
   * Increment the quantity of the product variant in the cart by 1.
   * If the current quantity is equal to the max quantity that can be added to the cart,
   * it won't do anything.
   * @param variant the product variant to add to the cart
   */
  incrementProductQuantity(variant: Variant): void {
    const cartItems: Cart[] = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => {
      return item.productId === this.product._id && item.variantId === variant._id;
    });
    if (cartItem) {
      if (this.isDelivery) {
        this.edeliveryService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      } else if (this.helper.route.url.includes("/users/create-booking/services")) {
        this.servicesService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      } else {
        this.qcommerceService.updateQuantity(this.product._id, variant._id, cartItem.qty + 1, variant.maxQtyAddInCart);
      }
    }
  }

  /**
   * Decreases the quantity of the specified product variant in the cart by 1.
   * If the quantity reaches 1 or less, the variant is removed from the cart.
   * Otherwise, the quantity is updated to the new value.
   * @param variant the product variant whose quantity is to be decreased
   */

  decrementProductQuantity(variant: Variant): void {
    const cartItems: Cart[] = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => {
      return item.productId === this.product._id && item.variantId === variant._id;
    });
    if (cartItem) {
      if (cartItem.qty > 1) {
        if (this.isDelivery) {
          this.edeliveryService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        } else if (this.helper.route.url.includes("/users/create-booking/services")) {
          this.servicesService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        } else {
          this.qcommerceService.updateQuantity(this.product._id, variant._id, cartItem.qty - 1, variant.maxQtyAddInCart);
        }
      } else {
        if (this.isDelivery) {
          this.edeliveryService.removeFromCart(this.product._id, variant._id);
        } else if (this.helper.route.url.includes("/users/create-booking/services")) {
          this.servicesService.removeFromCart(this.product._id, variant._id);
        } else {
          this.qcommerceService.removeFromCart(this.product._id, variant._id);
        }
      }
    }
  }

  /**
   * Get the quantity of a product variant in the cart.
   * @param variantId the id of the product variant to get the quantity of
   * @returns the quantity of the product variant in the cart, or 0 if it is not in the cart.
   */
  getVariantQuantity(variantId: string): number {
    const cartItems: Cart[] = this.getCartItems();
    const cartItem = cartItems.find((item) => item.productId === this.product._id && item.variantId === variantId);
    return cartItem ? cartItem.qty : 0;
  }

  private getCartItems(): Cart[] {
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      return JSON.parse(localStorage.getItem("cartItems") || "[]");
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      return JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    }
    return JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
  }

  /**
   * Returns the CSS style object for a button based on its configuration.
   * @param buttonConfig The button configuration object.
   * @returns An object with CSS styles for the button, including background color and color.
   */
  getButtonStyle(buttonConfig: ButtonConfig): { [key: string]: string } {
    return {
      backgroundColor: buttonConfig?.bgColor,
      color: buttonConfig?.color
    };
  }
}
